import styled, { css } from 'styled-components';

import { mixins } from '../../../styles/breakpoints';
import colors from '../../../styles/colors';
import { checkoutPagesFontStyles } from '../../../styles/text';
import { ContentContainer, LeftSideWrapper, StyledAppPreview } from '../../Home/styles';

/*
  The first-purchase layout drops the app preview column below tablet width to make room for
  the install steps. The upsell variant has far less to say, so it keeps the preview on phones,
  stacked above the copy and scaled down.
*/
const UpsellContentContainer = styled(ContentContainer)`
  ${mixins.upToTabletMedia(css`
    flex-direction: column;
    align-items: center;
    gap: 0;
    margin-top: 20px;
  `)}
`;

const UpsellPreviewWrapper = styled(LeftSideWrapper)`
  ${mixins.upToTabletMedia(css`
    display: flex;
    align-items: center;
    max-width: 100%;
    margin: 0;
  `)}
`;

const UpsellAppPreview = styled(StyledAppPreview)`
  ${mixins.upToTabletMedia(css`
    width: 260px;
    height: 260px;
  `)}
`;

const Container = styled.div`
  ${checkoutPagesFontStyles};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  background-color: ${colors.base.alpha};
  background-size: cover;
  overflow: hidden;
  scroll-behavior: smooth;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 90%;
  margin: 20px;
`;

const ErrorMessage = styled.p`
  ${checkoutPagesFontStyles};
  font-size: 14px;
  line-height: 24px;
  color: ${colors.shades.gamma14};
  margin: 20px 0;
`;

const LoadingMessage = styled.p`
  ${checkoutPagesFontStyles};
  font-size: 14px;
  line-height: 24px;
  color: ${colors.shades.gamma14};
  text-align: center;
  margin: 0;
`;

const EmailSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 24px;
`;

const EmailLabel = styled.p`
  ${checkoutPagesFontStyles};
  font-size: 14px;
  color: ${colors.shades.gamma14};
  margin: 0 0 5px 0;
`;

const EmailText = styled.p`
  ${checkoutPagesFontStyles};
  font-size: 16px;
  font-weight: 600;
  color: ${colors.base.beta};
  margin: 0;
  word-break: break-all;
`;

const WrongEmailSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 24px 0;
  width: 100%;
  box-sizing: border-box;
`;

const WrongEmailTitle = styled.p`
  ${checkoutPagesFontStyles};
  font-size: 14px;
  font-weight: 600;
  color: ${colors.base.danger};
  margin: 0 0 4px 0;
`;

const Description = styled.p`
  ${checkoutPagesFontStyles};
  font-size: 14px;
  line-height: 1.5;
  color: ${colors.shades.gamma10};
  margin: 0 0 12px 0;
`;

const ChangeEmailButton = styled.button`
  ${checkoutPagesFontStyles};
  background: none;
  border: 1px solid ${colors.base.beta};
  color: ${colors.base.beta};
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${colors.base.beta};
    color: ${colors.base.alpha};
  }
`;

// The account's email at the end of the sticker flow, kept on this page's light look on a coach's dark theme too.
const AccountPanel = styled.div`
  padding: 20px 16px;
  border-radius: 16px;
  background-color: ${colors.base.alpha};
`;

const SupportText = styled.p`
  ${checkoutPagesFontStyles};
  font-size: 14px;
  color: ${colors.shades.gamma14};
  margin: 24px 0 0 0;
  text-align: left;
  width: 100%;

  a {
    color: ${colors.base.beta};
    text-decoration: underline;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const ChangeEmailFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin: 12px 0;
  padding: 12px;
  border: 1px solid ${colors.shades.beta1};
  border-radius: 8px;
  box-sizing: border-box;
`;

const ChangeEmailFormTitle = styled.h3`
  ${checkoutPagesFontStyles};
  font-size: 14px;
  font-weight: 600;
  color: ${colors.base.beta};
  margin: 0 0 16px 0;
`;

const EmailInputLabel = styled.label`
  ${checkoutPagesFontStyles};
  font-size: 14px;
  color: ${colors.shades.gamma14};
  margin: 0 0 6px 0;
`;

const EmailInput = styled.input`
  ${checkoutPagesFontStyles};
  width: 100%;
  padding: 12px;
  font-size: 14px;
  border: 1px solid ${colors.shades.gamma5};
  border-radius: 6px;
  margin-bottom: 16px;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${colors.base.beta};
  }

  &:disabled {
    background-color: ${colors.shades.gamma2};
    cursor: not-allowed;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  margin-top: 8px;
`;

const SubmitButton = styled.button`
  ${checkoutPagesFontStyles};
  flex: 1;
  border: 1px solid ${colors.shades.gamma10};
  color: ${colors.shades.gamma10};
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:not(:disabled) {
    border: 1px solid ${colors.base.beta};
    background-color: ${colors.base.beta};
    color: ${colors.base.alpha};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  ${checkoutPagesFontStyles};
  flex: 1;
  background: none;
  border: 1px solid ${colors.base.beta};
  color: ${colors.base.beta};
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${colors.base.beta};
    color: ${colors.base.alpha};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormError = styled.p`
  ${checkoutPagesFontStyles};
  font-size: 14px;
  color: ${colors.base.danger};
  margin: 0 0 12px 0;
`;

const FormSuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 20px 0;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${colors.shades.success7};
  border-radius: 6px;
  padding: 12px 16px;
`;

const FormSuccessMessage = styled.p`
  ${checkoutPagesFontStyles};
  font-size: 15px;
  color: ${colors.shades.success7};
  margin: 0;
  text-align: center;
  width: 100%;
`;

const FormNewEmailMessage = styled.p`
  ${checkoutPagesFontStyles};
  font-size: 15px;
  color: ${colors.shades.success7};
  font-weight: 600;
  margin: 0;
  text-align: center;
  width: 100%;
`;

export {
  UpsellContentContainer,
  UpsellPreviewWrapper,
  UpsellAppPreview,
  Container,
  ErrorContainer,
  ErrorMessage,
  LoadingMessage,
  EmailSection,
  EmailLabel,
  EmailText,
  WrongEmailSection,
  WrongEmailTitle,
  Description,
  ChangeEmailButton,
  AccountPanel,
  SupportText,
  ChangeEmailFormContainer,
  ChangeEmailFormTitle,
  EmailInputLabel,
  EmailInput,
  ButtonsContainer,
  SubmitButton,
  CancelButton,
  FormError,
  FormSuccessContainer,
  FormSuccessMessage,
  FormNewEmailMessage,
};
